import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import type { PayloadAction } from "@reduxjs/toolkit";
import Constants from "expo-constants";
import { Address, AuthType } from "src/common/types/auth";
import { Post, PublicGet, StringToB64 } from "libs/services";
import { USER } from "libs/config";
import { setLoading } from "./loader";
import { openToast } from "./toast";
import { RefreshTokenPayloadResponse } from "src/common/types/response/refresh";
import { getMaster } from "./master";
import { USER_ROLES } from "src/common/libs/utils/dataUtils";
import translate from "src/common/libs/locale";
import { GUEST_ROLE } from "src/common/libs/utils/constants";
import { BaseResponse } from "src/common/types/response/BasePaginationResponse";
import { FCMTokenRegisterResponse } from "src/common/types/response/fcmTokenRegisterResponse";
import { reportLog } from "src/common/libs/utils/logs";

interface AuthState {
  data: AuthType;
  refreshedToken: string;
  pushNotificationData: {
    expPushToken: string;
  };
}

let authLoading = false;
const extras = Constants.expoConfig?.extra;
const client = extras?.client || process.env.CLIENT;

export const login = createAsyncThunk(
  "auth/login",
  async (
    {
      username,
      password,
      guest = false,
    }: { username?: string; password?: string; guest?: boolean },
    thunkAPI
  ) => {
    const encodedCredentials = !username
      ? `Basic ${process.env.EXPO_PUBLIC_USER_AUTH}`
      : StringToB64(`${username}:${password}`);
    const headers = { Authorization: encodedCredentials };
    if (authLoading && !username) {
      authLoading = false;
      return;
    }
    authLoading = true;
    try {
      !guest && thunkAPI.dispatch(setLoading(true));
      const URL = /^\d/.test(username) ? USER.loginOtp : USER.login;
      const response = await PublicGet(URL, null, headers);
      if (
        client === "customer" &&
        response.data?.payload?.user?.role !== GUEST_ROLE &&
        USER_ROLES.find((it) => it.name === "Customer").id !==
          response.data?.payload?.user?.role
      ) {
        thunkAPI.dispatch(
          openToast({ message: translate("loginWithCustomer") })
        );
        return null;
      } else if (
        client === "merchant" &&
        response.data?.payload?.user?.role !== GUEST_ROLE &&
        USER_ROLES.find((it) => it.name === "Merchant").id !==
          response.data?.payload?.user?.role
      ) {
        thunkAPI.dispatch(
          openToast({ message: translate("loginWithMerchant") })
        );
        return null;
      }
      if (response.data?.payload?.user?.role !== GUEST_ROLE) {
        await thunkAPI.dispatch(
          getMaster({
            headers: {
              Authorization: response.data?.payload?.t,
            },
          })
        );
      }
      const { first_name, last_name } = response.data?.payload?.user || {};
      if (
        response.data?.payload?.t &&
        first_name?.trim() &&
        last_name?.trim().length
      ) {
        return response.data?.payload;
      }
      return { temp: response.data?.payload };
    } catch (error) {
      reportLog({
        error: error,
        location: "features/auth->login[88]",
        body: { username },
      });
      thunkAPI.dispatch(
        openToast({ message: error.response?.data?.message || error.message })
      );
      throw error;
    } finally {
      !guest && thunkAPI.dispatch(setLoading(false));
      authLoading = false;
    }
  }
);

export const refreshToken = createAsyncThunk(
  "auth/refreshToken",
  async ({ rt }: { rt: string }, thunkAPI) => {
    const headers = { "refresh-token": rt };
    try {
      const response = await PublicGet(USER.refresh, null, headers);
      return response.data?.payload;
    } catch (error) {
      reportLog({
        error: error,
        location: "features/auth->refreshToken[117]",
        body: { headers },
      });
      thunkAPI.dispatch(
        openToast({ message: error.response?.data?.message || error.message })
      );
      throw error;
    } finally {
    }
  }
);

export const registerPushToken = createAsyncThunk(
  "auth/registerPushToken",
  async (
    { userId, expPushToken }: { userId: string; expPushToken: string },
    thunkAPI
  ) => {
    try {
      const response = await Post<BaseResponse<FCMTokenRegisterResponse>>(
        `${USER.registerPush}`,
        {
          userId,
          expPushToken,
        }
      );
      return response.data;
    } catch (error) {
      reportLog({
        error: error,
        location: "features/auth->registerPushToken[146]",
        body: { userId, expPushToken },
      });
      return null;
    }
  }
);

const initialState: AuthState = {
  data: null,
  refreshedToken: "pending",
  pushNotificationData: null,
};

export const authSlice = createSlice({
  name: "authentication",
  initialState,
  reducers: {
    setToken: (state, action: PayloadAction<AuthType>) => {
      state.data = action.payload;
    },
    updateProfileData: (state, action) => {
      state.data = {
        ...state.data,
        user: {
          ...state.data.user,
          ...action.payload,
        },
      };
    },
    updateToken: (state, action: PayloadAction<{ rt: string; t: string }>) => {
      state.data = {
        ...state.data,
        rt: action.payload.rt,
        t: action.payload.t,
      };
    },
    resetTokenData: (state, action) => {
      state.data = { ...action.payload };
    },
    clearToken: () => initialState,
    clearRefreshState: (state) => {
      state.refreshedToken = "pending";
    },
    updateAddress: (state, action: PayloadAction<Address>) => {
      state.data = {
        ...state.data,
        user: {
          ...state.data.user,
          address: action.payload,
        } as any,
      };
    },
    updateStoreServing: (state, action: PayloadAction<boolean>) => {
      state.data = {
        ...state.data,
        stores: [
          {
            ...state.data.stores[0],
            is_serving: action.payload,
          },
        ],
      };
    },
    updateStoreData: (state, action: PayloadAction<{}>) => {
      state.data = {
        ...state.data,
        stores: [
          {
            ...state.data.stores[0],
            ...action.payload,
          },
        ],
      };
    },
    updateMerchantImage: (state, action: PayloadAction<string>) => {
      state.data = {
        ...state.data,
        stores: [
          {
            ...state.data.stores[0],
            images: [action.payload],
          },
        ],
      };
    },
    clearData: () => initialState,
  },
  extraReducers: (builder) => {
    builder.addCase(login.fulfilled, (state, action) => {
      state.data = { ...action.payload };
      state.refreshedToken = "success";
    });
    builder.addCase(login.pending, (state) => {
      state.refreshedToken = "pending";
    });
    builder.addCase(login.rejected, (state) => {
      state.refreshedToken = "failed";
    });

    builder.addCase(
      refreshToken.fulfilled,
      (state, action: PayloadAction<RefreshTokenPayloadResponse>) => {
        state.data = { ...state.data, ...action.payload };
        state.refreshedToken = "success";
      }
    );
    builder.addCase(refreshToken.pending, (state) => {
      state.refreshedToken = "pending";
    });
    builder.addCase(refreshToken.rejected, (state) => {
      state.refreshedToken = "failed";
    });

    builder.addCase(
      registerPushToken.fulfilled,
      (state, action: PayloadAction<any>) => {
        state.pushNotificationData = {
          expPushToken: action?.payload?.payload?.expPushToken,
        };
      }
    );
    builder.addCase(registerPushToken.pending, (state) => {
      state.pushNotificationData = null;
    });
    builder.addCase(registerPushToken.rejected, (state) => {
      state.pushNotificationData = null;
    });
  },
});

export const {
  setToken,
  clearToken,
  clearRefreshState,
  updateAddress,
  clearData,
  updateToken,
  updateProfileData,
  updateStoreServing,
  updateMerchantImage,
  updateStoreData,
  resetTokenData,
} = authSlice.actions;

export default authSlice.reducer;
